import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { NotificationService } from './notification.service';
import { AuthResponseModel, TokenModel } from '../models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY   = 'cb_authorization_token';
  private readonly platformId  = inject(PLATFORM_ID);
  private readonly api         = inject(ApiService);
  private readonly userService = inject(UserService);
  private readonly notifications = inject(NotificationService);

  private _token = signal<TokenModel | null>(this.loadToken());

  readonly isAuthenticated = computed(() => {
    const token = this._token();
    if (!token) return false;
    return new Date(token.expires_in).valueOf() > new Date().valueOf();
  });

  readonly accessToken = computed(() => this._token()?.access_token ?? null);

  readonly username = computed(() => this._token()?.username ?? null);

  getToken(): TokenModel | null {
    return this._token();
  }

  setToken(token: TokenModel): void {
    const expiresAt = Date.now() + token.expires_in * 1000;
    const stored: TokenModel = { ...token, expires_in: expiresAt };
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.TOKEN_KEY, JSON.stringify(stored));
    }
    this._token.set(stored);
  }

  login(phone: string, password: string): Observable<void> {
    return this.api
      .post<AuthResponseModel>('login', { username: phone, password })
      .pipe(
        tap(res => this.setToken({ ...res })),
        switchMap(res => this.userService.loadCurrentUser(res.userId).pipe(
          catchError(() => EMPTY),
        )),
      );
  }

  // Call after any action that changes the user's profile (profile save, avatar upload).
  // Silently swallows errors — the header degrades gracefully to the initials fallback.
  refreshUser(): Observable<void> {
    const userId = this._token()?.userId;
    if (!userId) return EMPTY;
    return this.userService.loadCurrentUser(userId).pipe(
      catchError(() => EMPTY),
    );
  }

  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.TOKEN_KEY);
    }
    this._token.set(null);
    this.userService.clear();
    this.notifications.reset();
  }

  isTokenExpired(): boolean {
    return !this.isAuthenticated();
  }

  private loadToken(): TokenModel | null {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return null;
    try {
      const raw = localStorage.getItem(this.TOKEN_KEY);
      return raw ? (JSON.parse(raw) as TokenModel) : null;
    } catch {
      return null;
    }
  }
}
